import * as React from "react";
import { ISubscriptionFilter, IStatus, IUser, ICalendar } from "../../models/get";
import { Button, ButtonType } from "../button";
import { TextBox } from "../textBox";
import { IComboBoxValue } from "../../models/form";
import { ComboBox, DROPDOWN_TEXT_ITEM_HEIGHT, ComboBoxType, DropDownTextItem } from "../comboBox";
import { InputSize } from "../input";
import { Alert, AlertType } from "../alert";
import * as globals from "../../globals";

enum AvailableFields
{
    Ersteller = "UserId",
    Kalender = "CalendarId",
    Status = "StatusId",
    Bestatter = "Persons.Any(IsMortician == true And Id"
}

interface IProperties
{
    label?: string;
    filters: ISubscriptionFilter[];
    onNewClause?: (index?: number) => void;
    onRemoveClause?: (index: number) => void;
    onClauseChange?: (filter: ISubscriptionFilter) => void;
    isGlobalRule?: boolean;
    isDisabled?: boolean;
}

interface IState
{
    currentUser?: IUser;
    calendars: ICalendar[];
    calendarsForFilter: ICalendar[];
    status: IStatus[];
    users: IUser[];
    morticians: IUser[];
}

const SententialOperators: IComboBoxValue<string, string>[] = [
    {
        selectedValue: "And",
        value: "Und"
    },
    {
        selectedValue: "Or",
        value: "Oder"
    }
]

const Fields: IComboBoxValue<string, string>[] = [
    {
        selectedValue: AvailableFields.Ersteller,
        value: "Ersteller"
    },
    {
        selectedValue: AvailableFields.Kalender,
        value: "Kalender"
    },
    {
        selectedValue: AvailableFields.Status,
        value: "Status"
    },
    {
        selectedValue: AvailableFields.Bestatter,
        value: "Bestatter"
    }
]

const Operators: IComboBoxValue<string, string>[] = [
    {
        selectedValue: "==",
        value: "Ist gleich"
    }
]

export class FilterCriteria extends React.Component<IProperties, IState>
{
    state: IState = {
        calendars: [],
        calendarsForFilter: [],
        status: [],
        users: [],
        morticians: []
    };

    async componentWillMount()
    {
        const { isGlobalRule } = this.props;
        const currentUser = await globals.getCurrentUserAsync();
        const calendars = await globals.getCalendarsAsync();
        const status = await globals.getStatusAsync();
        const allUsers = await globals.getUsersAsync();
        const calendarsForFilter = [...calendars];
        const users = allUsers.where(u => !u.is_deactivated && (!this.props.isGlobalRule && u.id !== currentUser.id));
        const morticians = allUsers.where(u => u.mortician_id > 0 && !u.is_deactivated);

        if (isGlobalRule)
        {
            calendarsForFilter.unshift({
                id: -1,
                name: "[Abonnent]"
            });

            users.unshift({
                id: -1,
                fullname: "[Abonnent]"
            });

            morticians.unshift({
                id: -1,
                fullname: "[Bestatter]"
            });
        }
        else
        {
            users.unshift({
                id: -1,
                fullname: "[Ich]"
            });

            morticians.unshift({
                id: -1,
                fullname: "[Bestatter]"
            });
        }

        this.setState({
            currentUser,
            calendars,
            calendarsForFilter,
            status,
            users,
            morticians
        });
    }

    private getLabel()
    {
        if (this.props.label != null)
        {
            return <label>{this.props.label}</label>
        }
    }

    private getSelectedSententialOperator(operator: string) {
        if (!operator || operator === "None")
            return {};

        return SententialOperators.where(s => s.selectedValue === operator).first();
    }

    private getField(field: string) {
        if (!field)
            return {};

        return Fields.where(f => f.selectedValue === field).first();
    }

    private getOperator(operator: string) {
        if (!operator)
            return {};

        return Operators.where(o => o.selectedValue === operator).first();
    }

    private onChange(filter: ISubscriptionFilter, key: string[], value: any)
    {
        const { onClauseChange } = this.props;
        key.forEach(k => filter[k] = value);
        onClauseChange && onClauseChange(filter);
    }

    private getValueField(filter: ISubscriptionFilter)
    {
        const { isDisabled } = this.props;
        const { currentUser, calendars, calendarsForFilter, status, users, morticians } = this.state;
        switch (filter.field)
        {
            case AvailableFields.Kalender:
                {
                    const selectedCalendar: ICalendar = calendarsForFilter.where(c => c.id === (filter.value_parsed > 0 ? filter.value_parsed : -1)).first() || {};
                    return (
                        <ComboBox
                            size={InputSize.normal}
                            dataSource={calendarsForFilter}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={selectedCalendar.name}
                            selectedValue={selectedCalendar.id}
                            valueMember="id"
                            displayMember="name"
                            isDisabled={isDisabled || filter.disabled}
                            onSelectionChanged={(item: ICalendar) => this.onChange({ ...filter }, ["value", "value_parsed"], (item.id < 0 ? item.name : item.id))}>
                            {
                                (item: ICalendar) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                    );
                }
            case AvailableFields.Ersteller:
                {
                    const selectedUser: IUser = users.where(m => m.id === (filter.value_parsed > 0 ? filter.value_parsed : -1)).first() || {};
                    return (
                        <ComboBox
                            size={InputSize.normal}
                            dataSource={users}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={selectedUser.fullname}
                            selectedValue={selectedUser.id}
                            valueMember="id"
                            displayMember="fullname"
                            isDisabled={isDisabled || filter.disabled}
                            onSelectionChanged={(item: IUser) => this.onChange({ ...filter }, ["value", "value_parsed"], (item.id < 0 ? item.fullname : item.id))}>
                            {
                                (item: IUser) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.fullname} />
                                    );
                                }
                            }
                        </ComboBox>
                    );
                }
            case AvailableFields.Status:
                {
                    const selectedStatus: IStatus = status.where(s => s.id === filter.value_parsed).first() || {};
                    return (
                        <ComboBox
                            size={InputSize.normal}
                            dataSource={status}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={selectedStatus.status}
                            selectedValue={selectedStatus.id}
                            valueMember="id"
                            displayMember="status"
                            isDisabled={isDisabled || filter.disabled}
                            onSelectionChanged={(item: IStatus) => this.onChange({ ...filter }, ["value", "value_parsed"], item.id)}>
                            {
                                (item: IStatus) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.status} />
                                    );
                                }
                            }
                        </ComboBox>
                    );
                }
            case AvailableFields.Bestatter:
                {
                    const selectedMortician: IUser = morticians.where(m => m.id === (filter.value_parsed > 0 ? filter.value_parsed : -1)).first() || {};
                    return (
                        <ComboBox
                            size={InputSize.normal}
                            dataSource={(currentUser.mortician_id > 0 ? morticians.where(m => m.id === -1) : morticians)}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={selectedMortician.fullname}
                            selectedValue={selectedMortician.id}
                            valueMember="id"
                            displayMember="fullname"
                            isDisabled={isDisabled || filter.disabled}
                            onSelectionChanged={(item: IUser) => this.onChange({ ...filter }, ["value", "value_parsed"], (item.id < 0 ? item.fullname : item.id))}>
                            {
                                (item: IUser) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.fullname} />
                                    );
                                }
                            }
                        </ComboBox>
                    );
                }
            default:
                return <TextBox isDisabled={true} />
        }
    }

    private getFilters()
    {
        const { filters, onNewClause, onRemoveClause, onClauseChange, isDisabled } = this.props;

        if (filters == null || filters.length == 0 && !isDisabled)
            return <Alert isVisible={true} alertType={AlertType.info}>Es sind keine Filter definiert. Fügen Sie Filterkriterien hinzu um die Benachrichtigung auf Ihre bedürfnisse einzustellen.</Alert>;

        return filters.map((f, i) => (
            <div
                key={f.id}
                className="filtercriteria_item"
            >
                <Button
                    icon="plus"
                    type={ButtonType.primary}
                    style={{ height: 30 }}
                    onClick={() => onNewClause(i)}
                    isDisabled={isDisabled || f.disabled}
                />
                <Button
                    icon="close"
                    type={ButtonType.danger}
                    style={{ height: 30 }}
                    onClick={() => onRemoveClause(i)}
                    isDisabled={isDisabled || f.disabled}
                />
                <ComboBox
                    size={InputSize.normal}
                    dataSource={SententialOperators}
                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                    comboBoxType={ComboBoxType.dropDownList}
                    value={this.getSelectedSententialOperator(f.sentential_operator).value}
                    selectedValue={this.getSelectedSententialOperator(f.sentential_operator).selectedValue}
                    valueMember="selectedValue"
                    displayMember="value"
                    isDisabled={isDisabled || (f.sentential_operator == undefined) || f.disabled}
                    onSelectionChanged={(item) => this.onChange({ ...f }, ["sentential_operator"], item.selectedValue)}>
                    {
                        (item: IComboBoxValue<string, string>) => {
                            return (
                                <DropDownTextItem
                                    key={item.selectedValue}
                                    value={item.selectedValue}
                                    displayValue={item.value} />
                            );
                        }
                    }
                </ComboBox>
                <ComboBox
                    size={InputSize.normal}
                    dataSource={Fields}
                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                    comboBoxType={ComboBoxType.dropDownList}
                    value={this.getField(f.field).value}
                    selectedValue={this.getField(f.field).selectedValue}
                    valueMember="selectedValue"
                    displayMember="value"
                    isDisabled={isDisabled || f.disabled}
                    onSelectionChanged={(item) => this.onChange({ ...f }, ["field"], item.selectedValue)}>
                    {
                        (item: IComboBoxValue<string, string>) => {
                            return (
                                <DropDownTextItem
                                    key={item.selectedValue}
                                    value={item.selectedValue}
                                    displayValue={item.value} />
                            );
                        }
                    }
                </ComboBox>
                <ComboBox
                    size={InputSize.normal}
                    dataSource={Operators}
                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                    comboBoxType={ComboBoxType.dropDownList}
                    value={this.getOperator(f.operator).value}
                    selectedValue={this.getOperator(f.operator).selectedValue}
                    valueMember="selectedValue"
                    displayMember="value"
                    isDisabled={isDisabled || f.disabled}
                    onSelectionChanged={(item) => this.onChange({ ...f }, ["operator"], item.selectedValue)}>
                    {
                        (item: IComboBoxValue<string, string>) => {
                            return (
                                <DropDownTextItem
                                    key={item.selectedValue}
                                    value={item.selectedValue}
                                    displayValue={item.value} />
                            );
                        }
                    }
                </ComboBox>
                {this.getValueField(f)}
            </div>
        ));
    }

    public render(): JSX.Element
    {
        const { filters, onNewClause, isDisabled } = this.props;
        const { currentUser } = this.state;

        if (currentUser == null)
            return null;

        return (
            <div className="filtercriteria">
                {this.getLabel()}
                {(filters != null && filters.length > 0) &&
                    <div className="filtercriteria_labels">
                        <div></div>
                        <label>Und/Oder</label>
                        <label>Feld</label>
                        <label>Operator</label>
                        <label>Wert</label>
                    </div>
                }
                {this.getFilters()}
                {!isDisabled &&
                    <a
                        className="filtercriteria_add-link"
                        onClick={() => onNewClause()}
                    >
                        <i className="mdi mdi-plus mdi-18px"></i>
                        Neue Klausel hinzufügen
                    </a>
                }
            </div>
        );
    }
}